import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import { Member, LoginCredentials, AuthContextType } from '../types/auth.types';
import errorLogger from '../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('AuthContext');

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  logger.debug('MOUNTING at ' + new Date().toISOString());
  
  React.useEffect(() => {
    return () => {
      logger.debug('UNMOUNTING at ' + new Date().toISOString());
    };
  }, []);
  
  // Initialize member from storage if available AND we have a valid token
  const getInitialMember = () => {
    // First check if we have a token
    const hasToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (!hasToken) {
      logger.debug('No token found, not loading member');
      return null;
    }
    
    // Try to get member from the correct key (member_data)
    const storedMember = localStorage.getItem('member_data') || localStorage.getItem('member') || sessionStorage.getItem('member');
    if (storedMember) {
      try {
        const member = JSON.parse(storedMember);
        logger.debug('Loaded stored member: ' + member.email);
        return member;
      } catch (e) {
        logger.error('Failed to parse stored member:', e);
      }
    }
    return null;
  };
  
  const [member, setMember] = useState<Member | null>(getInitialMember());
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Debug member state changes
  useEffect(() => {
    logger.debug('Member state changed: ' + (member?.email || 'null'));
  }, [member]);

  // Check if member is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        logger.debug('Checking authentication...');
        
        // Clear any stale member data if no token exists
        const hasToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        if (!hasToken) {
          logger.debug('No token found, clearing any stale member data');
          localStorage.removeItem('member');
          localStorage.removeItem('member_data');
          sessionStorage.removeItem('member');
          setMember(null);
          setIsLoading(false);
          return;
        }
        
        if (authService.isAuthenticated()) {
          logger.debug('User is authenticated');
          // First try to get member from localStorage
          const storedMember = authService.getMember();
          logger.debug('Stored member:', storedMember);
          if (storedMember) {
            setMember(storedMember);
          } else {
            logger.warning('No stored member found despite having token');
          }
          
          // Then try to refresh token to get fresh member data
          try {
            const { member } = await authService.refreshToken();
            setMember(member);
          } catch (refreshError) {
            // If refresh fails but we have stored member data, keep using it
            if (!storedMember) {
              throw refreshError;
            }
          }
        }
      } catch (error) {
        logger.error('Auth check failed:', error);
        // Don't automatically logout - let the user stay on login page
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const result = await authService.login(credentials);
      logger.debug('Login result:', result);
      logger.debug('Has twoFactorRequired? ' + result.twoFactorRequired);
      logger.debug('Has member? ' + !!result.member);
      
      // Check if 2FA is required
      if (result.twoFactorRequired) {
        logger.debug('Returning 2FA required response');
        // Return the 2FA details so the Login component can handle it
        return {
          twoFactorRequired: true,
          userId: result.userId,
          method: result.method
        };
      }
      
      // If we have a member, login was successful
      if (result.member) {
        setMember(result.member);
        navigate('/voicemail');
        return { success: true, member: result.member };
      } else {
        // If no member but also no error, might be a response format issue
        logger.error('No member in result:', result);
        throw new Error('Login failed - no member data');
      }
    } catch (error) {
      logger.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    // Suppress error notifications during logout
    errorLogger.setLoggingOut(true);

    try {
      await authService.logout();
      setMember(null);
      navigate('/login');
    } catch (error) {
      // Log to console but don't show user notification since logout still succeeds
      logger.debug('Logout API call failed (continuing with logout):', error);
      // Even if logout API fails, clear local state and redirect
      setMember(null);
      navigate('/login');
    } finally {
      // Re-enable error notifications after navigation completes
      setTimeout(() => {
        errorLogger.setLoggingOut(false);
      }, 500);
    }
  };

  // Function to complete login after 2FA verification
  const completeTwoFactorLogin = (member: Member) => {
    logger.debug('Completing 2FA login with member:', member);
    setMember(member);
    navigate('/voicemail');
  };

  const refreshToken = async () => {
    try {
      const { member } = await authService.refreshToken();
      setMember(member);
    } catch (error: any) {
      logger.error('refreshToken failed:', error);
      // Only clear auth and redirect on definitive auth failure
      if (error.response?.status === 401) {
        setMember(null);
        navigate('/login');
      }
      throw error;
    }
  };

  const value: AuthContextType = {
    member,
    isAuthenticated: !!member,
    isLoading,
    login,
    logout,
    refreshToken,
    completeTwoFactorLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};